import React, { useEffect, useState } from 'react';
import { 
  Form, 
  Input, 
  Button, 
  Card, 
  Typography, 
  Alert, 
  message, 
  Select, 
  DatePicker, 
  Row, 
  Col, 
  Space, 
  Divider,
  Progress,
  Avatar,
  Badge
} from 'antd';
import { 
  UserOutlined, 
  MailOutlined, 
  PhoneOutlined, 
  CalendarOutlined, 
  BookOutlined, 
  TeamOutlined,
  CheckCircleOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import dayjs from 'dayjs';

import { useAuth } from '../../../contexts/AuthContext';
import useCreateStudentSelfOnboarding from '../../../api/CreateStudentSelfOnboarding';
import { useCountryDetection } from '../../../hooks/useCountryDetection';

const { Title, Text } = Typography;

const StudentProfileCompletion = () => {

  console.log('StudentProfileCompletion - User data:');
  const [form] = Form.useForm();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [disableEmail, setDisableEmail] = useState(false);
  const [disablePhone, setDisablePhone] = useState(false);
  const history = useHistory();
  const { user, isAuthenticated, refreshUser } = useAuth();
  const { countryCode } = useCountryDetection();
  
  // Get institute_id from Redux store (stored during validation in StudentAuth)
  const authParams = useSelector(state => state.authParams);
  const instituteIdFromRedux = authParams?.institute_id || sessionStorage.getItem('institute_id');
  
  const { createStudent, response, error: apiError, isLoading: submitting } = useCreateStudentSelfOnboarding();

  // Log when institute_id is available from Redux (for debugging)
  useEffect(() => {
    if (instituteIdFromRedux) {
      console.log('Institute ID retrieved from Redux/sessionStorage:', instituteIdFromRedux);
      console.log('Institute code field will be hidden and auto-filled on submit');
    }
  }, [instituteIdFromRedux]);

  useEffect(() => {
    console.log('StudentProfileCompletion - User data:', user);
    console.log('StudentProfileCompletion - Is authenticated:', isAuthenticated);
    console.log('StudentProfileCompletion - Loading state:', loading);
    
    if (user && isAuthenticated) {
      // Pre-fill the form with user data from AuthContext
      const userName = user.name || 
        (user.given_name && user.family_name ? `${user.given_name} ${user.family_name}` : '') ||
        user.email?.split('@')[0] || '';
      
      // Disable individual fields if they already exist in profile
      const hasEmail = user.email && user.email.trim() !== '';
      const hasPhone = user.phone_number && user.phone_number.trim() !== '';

      setDisableEmail(!!hasEmail);
      setDisablePhone(!!hasPhone);
      
      form.setFieldsValue({
        email: user.email || '',
        phoneNumber: user.phone_number || '',
        name: userName
      });
      setPhoneNumber(user.phone_number || '');
      setLoading(false);
    } else if (!isAuthenticated) {
      // User is not authenticated, redirect to auth
      console.log('User not authenticated, redirecting to auth');
      const clientLoginBaseUrl = sessionStorage.getItem('client_base_url');
      if (clientLoginBaseUrl) {
        window.location.href = clientLoginBaseUrl;
      } else {
        history.push('/auth');
      }
    } else {
      // Still loading user data
      console.log('Still loading user data...');
    }
  }, [user, isAuthenticated, form, history]);

  // Add timeout to prevent infinite loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        console.log('Loading timeout reached, stopping loading state');
        setLoading(false);
      }
    }, 10000); // 10 second timeout

    return () => clearTimeout(timeout);
  }, [loading]);

  // Handle successful profile creation
  useEffect(() => {
    if (response) {
      message.success('Student profile created successfully!');
      // Refresh user data to update roles/profile status
      refreshUser();
      history.push('/admin');
    }
  }, [response, history]);
 
  // Handle API errors
  useEffect(() => {
    if (apiError) {
      const detail = apiError.response?.data?.detail;
      const statusCode = apiError.response?.status;
      console.log(detail)
      
      // Handle specific institute code error: {detail: "Invalid institute code: 54544"} with status 400
      if (statusCode === 400 && detail && typeof detail === 'string' && detail.toLowerCase().includes('invalid institute code')) {
        form.setFields([
          {
            name: "institute_code",
            errors: [detail],
          },
        ]);
        return;
      }
      
      // Handle 422 validation errors with array format
      if (statusCode === 422 && Array.isArray(detail)) {
        detail.forEach((err) => {
          const fieldName = err.loc?.[1]; // e.g., "phone", "institute_code"
          const msg = err.msg || 'Invalid value';
          
          // Map API field names to form field names
          let formFieldName = fieldName;
          if (fieldName === "phone") {
            formFieldName = "phoneNumber";
          } else if (fieldName === "institute_code") {
            formFieldName = "institute_code";
          }
  
          if (fieldName) {
            form.setFields([
              {
                name: formFieldName,
                errors: [msg],
              },
            ]);
          }
        });
        return;
      }
      
      if (Array.isArray(detail)) {
        // Map backend errors to form fields
        detail.forEach((err) => {
          const fieldName = err.loc?.[1]; // e.g., "phone"
          const msg = err.msg || 'Invalid value';
  
          if (fieldName) {
            form.setFields([
              {
                name: fieldName === "phone" ? "phoneNumber" : fieldName, // map API field name to your form field name
                errors: [msg],
              },
            ]);
          }
        });
      } else {
        setError(apiError.response?.data?.message || 'Failed to create student profile. Please try again.');
      }
    }
  }, [apiError, form]);

  const handleFinish = (values) => {
    setError('');
    
    // Use the prefilled values if a contact field is disabled, otherwise use form values
    const emailToUse = disableEmail ? (user?.email || values.email) : values.email;
    const phoneToUse = disablePhone ? (phoneNumber || user?.phone_number) : phoneNumber;
    
    // Use institute_id from Redux as institute_code (auto-filled, user doesn't see this)
    const instituteCodeToUse = instituteIdFromRedux || values.institute_code;
    
    console.log('Submitting with institute_code from Redux:', instituteCodeToUse);
    
    // Create student profile using the API
    createStudent(
      values.name,
      values.grade,
      emailToUse,
      instituteCodeToUse, // Use institute_id from Redux instead of form value
      '', // client - removed from form
      values.dob ? dayjs(values.dob).format('YYYY-MM-DD') : '',
      null, // response_id - removed from form
      null, // client_id - removed from form
      phoneToUse,
      values.division || '',
      '' // coupon_code - removed from form
    );
  };

  // Show loading state while fetching user data
  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <Card 
          style={{ 
            maxWidth: 400, 
            textAlign: 'center',
            borderRadius: '16px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            border: 'none'
          }}
        >
          <div style={{ padding: '40px 20px' }}>
            <LoadingOutlined style={{ fontSize: '48px', color: '#667eea', marginBottom: '20px' }} />
            <Title level={4} style={{ color: '#333', marginBottom: '8px' }}>
              Setting up your profile
            </Title>
            <Text type="secondary">
              Please wait while we load your information...
            </Text>
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <Alert 
          type="error" 
          message={error} 
          style={{ 
            maxWidth: 500, 
            borderRadius: '12px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
          }} 
        />
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          /* Modern Input Styling */
          .modern-input {
            border: 2px solid #e8e8e8 !important;
            border-radius: 12px !important;
            padding: 12px 16px !important;
            font-size: 16px !important;
            transition: all 0.3s ease !important;
            box-shadow: 0 2px 8px rgba(0,0,0,0.06) !important;
            background: #fff !important;
            height: 48px !important;
            line-height: 1.5 !important;
          }
          
          .modern-input:hover {
            border-color: #d9d9d9 !important;
            box-shadow: 0 4px 12px rgba(0,0,0,0.08) !important;
          }
          
          .modern-input:focus,
          .modern-input.ant-input-focused {
            border-color: #667eea !important;
            box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
            outline: none !important;
          }
          
          .modern-input::placeholder {
            color: #999 !important;
            font-size: 16px !important;
          }
          
          /* Modern Date Picker Styling */
          .modern-date-picker {
            border: 2px solid #e8e8e8 !important;
            border-radius: 12px !important;
            padding: 8px 12px !important;
            font-size: 16px !important;
            transition: all 0.3s ease !important;
            box-shadow: 0 2px 8px rgba(0,0,0,0.06) !important;
            background: #fff !important;
            height: 48px !important;
            width: 100% !important;
          }
          
          .modern-date-picker:hover {
            border-color: #d9d9d9 !important;
            box-shadow: 0 4px 12px rgba(0,0,0,0.08) !important;
          }
          
          .modern-date-picker:focus,
          .modern-date-picker.ant-picker-focused {
            border-color: #667eea !important;
            box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
            outline: none !important;
          }
          
          .modern-date-picker .ant-picker-input {
            height: 32px !important;
            display: flex !important;
            align-items: center !important;
          }
          
          .modern-date-picker .ant-picker-input > input {
            font-size: 16px !important;
            color: #333 !important;
            height: 32px !important;
            line-height: 1.5 !important;
            padding: 0 !important;
          }
          
          .modern-date-picker .ant-picker-suffix {
            color: #667eea !important;
            display: flex !important;
            align-items: center !important;
          }
          
          .modern-date-picker .ant-picker-clear {
            display: flex !important;
            align-items: center !important;
          }
          
          /* Modern Select Styling */
          .modern-select .ant-select-selector {
            border: 2px solid #e8e8e8 !important;
            border-radius: 12px !important;
            padding: 8px 12px !important;
            font-size: 16px !important;
            transition: all 0.3s ease !important;
            box-shadow: 0 2px 8px rgba(0,0,0,0.06) !important;
            background: #fff !important;
            height: 48px !important;
            display: flex !important;
            align-items: center !important;
            min-height: 48px !important;
          }
          
          .modern-select:hover .ant-select-selector {
            border-color: #d9d9d9 !important;
            box-shadow: 0 4px 12px rgba(0,0,0,0.08) !important;
          }
          
          .modern-select.ant-select-focused .ant-select-selector {
            border-color: #667eea !important;
            box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
          }
          
          .modern-select .ant-select-selection-item {
            font-size: 16px !important;
            color: #333 !important;
            line-height: 1.5 !important;
            display: flex !important;
            align-items: center !important;
            height: 32px !important;
          }
          
          .modern-select .ant-select-selection-placeholder {
            font-size: 16px !important;
            color: #999 !important;
            line-height: 1.5 !important;
            display: flex !important;
            align-items: center !important;
            height: 32px !important;
          }
          
          .modern-select .ant-select-arrow {
            color: #667eea !important;
            right: 12px !important;
            top: 50% !important;
            font-size: 14px !important;
          }
          .ant-picker {
            min-width: 100% !important;
          }
          
          .modern-select .ant-select-selection-search {
            display: flex !important;
            align-items: center !important;
            height: 32px !important;
          }
          
          .modern-select .ant-select-selection-search-input {
            font-size: 16px !important;
            color: #333 !important;
            height: 32px !important;
            line-height: 1.5 !important;
            padding: 0 !important;
          }
          
          .modern-select .ant-select-selection-item-remove {
            display: flex !important;
            align-items: center !important;
            height: 32px !important;
          }
          
          /* Modern Phone Input Styling */
          .modern-phone-input {
            border: 2px solid #e8e8e8 !important;
            border-radius: 12px !important;
            transition: all 0.3s ease !important;
            box-shadow: 0 2px 8px rgba(0,0,0,0.06) !important;
            background: #fff !important;
            height: 48px !important;
            display: flex !important;
            align-items: center !important;
            padding: 0 12px !important;
            width: 100% !important;
          }
          
          .modern-phone-input:hover {
            border-color: #d9d9d9 !important;
            box-shadow: 0 4px 12px rgba(0,0,0,0.08) !important;
          }
          
          .modern-phone-input:focus-within {
            border-color: #667eea !important;
            box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
          }
          
          .modern-phone-input .PhoneInputCountry {
            margin-right: 8px !important;
            display: flex !important;
            align-items: center !important;
          }
          
          .modern-phone-input .PhoneInputCountrySelect {
            border: none !important;
            background: transparent !important;
            padding: 0 !important;
            margin: 0 !important;
            height: 32px !important;
            display: flex !important;
            align-items: center !important;
          }
          
          .modern-phone-input .PhoneInputCountryIcon {
            width: 20px !important;
            height: 15px !important;
            border-radius: 2px !important;
            margin-right: 4px !important;
          }
          
          .modern-phone-input .PhoneInputCountrySelectArrow {
            color: #667eea !important;
            margin-left: 4px !important;
            font-size: 12px !important;
          }
          
          .modern-phone-input input {
            font-size: 16px !important;
            color: #333 !important;
            border: none !important;
            box-shadow: none !important;
            background: transparent !important;
            flex: 1 !important;
            height: 32px !important;
            line-height: 1.5 !important;
            padding: 0 !important;
          }
          
          .modern-phone-input input:focus {
            outline: none !important;
            box-shadow: none !important;
          }
          
          .modern-phone-input input::placeholder {
            color: #999 !important;
            font-size: 16px !important;
          }
          
          /* Dropdown Styling */
          .ant-select-dropdown {
            border-radius: 12px !important;
            box-shadow: 0 8px 24px rgba(0,0,0,0.12) !important;
            border: 1px solid #e8e8e8 !important;
            padding: 8px !important;
          }
          
          .ant-picker-dropdown {
            border-radius: 12px !important;
            box-shadow: 0 8px 24px rgba(0,0,0,0.12) !important;
            border: 1px solid #e8e8e8 !important;
          }
          
          .ant-picker-panel {
            border: none !important;
          }
          
          .ant-picker-header {
            border-bottom: 1px solid #f0f0f0 !important;
          }
          
          .ant-picker-body {
            padding: 8px !important;
          }
          
          .ant-picker-cell {
            border-radius: 8px !important;
            margin: 2px !important;
          }
          
          .ant-picker-cell:hover .ant-picker-cell-inner {
            background-color: rgba(102, 126, 234, 0.1) !important;
            border-radius: 8px !important;
          }
          
          .ant-picker-cell-selected .ant-picker-cell-inner {
            background-color: #667eea !important;
            border-radius: 8px !important;
          }
          
          .ant-select-item {
            border-radius: 8px !important;
            margin: 4px 8px !important;
            transition: all 0.2s ease !important;
            padding: 12px 16px !important;
          }
          
          .ant-select-item:hover {
            background-color: rgba(102, 126, 234, 0.1) !important;
          }
          
          .ant-select-item-option-selected {
            background-color: rgba(102, 126, 234, 0.15) !important;
            color: #667eea !important;
            font-weight: 600 !important;
          }
          
          /* Form Item Styling */
          .ant-form-item {
            margin-bottom: 24px !important;
          }
          
          .ant-form-item-label {
            padding-bottom: 8px !important;
          }
          
          .ant-form-item-label > label {
            font-size: 14px !important;
            font-weight: 600 !important;
            color: #333 !important;
            height: auto !important;
            line-height: 1.4 !important;
          }
          
          .ant-form-item-label > label.ant-form-item-required::before {
            color: #ff4d4f !important;
            font-size: 14px !important;
            margin-right: 4px !important;
          }
          
          /* Badge Styling */
          .ant-badge-status-text {
            font-size: 12px !important;
            color: #52c41a !important;
            font-weight: 500 !important;
          }
          
          /* Space Component Styling */
          .ant-space {
            display: flex !important;
            align-items: center !important;
          }
          
          .ant-space-item {
            display: flex !important;
            align-items: center !important;
          }
        `}
      </style>
      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px'
      }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header Section */}
        <Card 
          style={{ 
            marginBottom: '24px',
            borderRadius: '16px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            border: 'none',
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Avatar 
                  size={80} 
                  icon={<UserOutlined />} 
                  style={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    marginBottom: '16px'
                  }} 
                />
                <Title level={2} style={{ margin: '0 0 8px 0', color: '#333' }}>
                  Complete Your Student Profile
                </Title>
                {user && (
                  <Text type="secondary" style={{ fontSize: '16px' }}>
                    Welcome back, <strong>{user.name || user.email}</strong>! Let&apos;s get you set up.
                  </Text>
                )}
              </div>
              
              <Progress 
                percent={60} 
                strokeColor={{
                  '0%': '#667eea',
                  '100%': '#764ba2',
                }}
                style={{ width: '300px', margin: '0 auto' }}
              />
              
              <Text type="secondary" style={{ fontSize: '14px' }}>
                Just a few more details to complete your profile
              </Text>
            </Space>
          </div>
        </Card>

        {/* Form Section */}
        <Card 
          style={{ 
            borderRadius: '16px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            border: 'none',
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleFinish}
            style={{ padding: '32px' }}
            size="large"
          >
            {/* Personal Information Section */}
            <div style={{ marginBottom: '32px' }}>
              <Title level={4} style={{ color: '#333', marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
                <UserOutlined style={{ marginRight: '8px', color: '#667eea' }} />
                Personal Information
              </Title>
              
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Form.Item 
                    label={
                      <Space>
                        <MailOutlined style={{ color: '#667eea' }} />
                        Email Address
                        {disableEmail && <Badge status="success" text="Verified" />}
                      </Space>
                    }
                    name="email"
                    rules={disableEmail ? [] : [
                      { required: true, message: 'Please enter your email' },
                      { type: 'email', message: 'Please enter a valid email address' },
                    ]}
                  >
                    <Input 
                      disabled={disableEmail} 
                      placeholder={disableEmail ? "Your verified email" : "Enter your email"}
                      size="large"
                      className="modern-input"
                      style={{ height: '48px' }}
                    />
                  </Form.Item>
                </Col>
                
                <Col xs={24} md={12}>
                  <Form.Item 
                    label={
                      <Space>
                        <PhoneOutlined style={{ color: '#667eea' }} />
                        Phone Number
                        {disablePhone && <Badge status="success" text="Verified" />}
                      </Space>
                    }
                    name="phoneNumber"
                    rules={disablePhone ? [] : [
                      { required: true, message: 'Please enter your phone number' },
                      {
                        validator: () => {
                          if (!phoneNumber) {
                            return Promise.reject(new Error('Please enter a valid phone number'));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <PhoneInput
                      placeholder={disablePhone ? "Your verified phone number" : "Enter your phone number"}
                      value={phoneNumber}
                      onChange={disablePhone ? undefined : setPhoneNumber}
                      disabled={disablePhone}
                      defaultCountry={countryCode}
                      international
                      countryCallingCodeEditable={false}
                      className="modern-phone-input"
                    />
                  </Form.Item>
                </Col>
              </Row>
              
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={
                      <Space>
                        <UserOutlined style={{ color: '#667eea' }} />
                        Full Name
                      </Space>
                    }
                    name="name"
                    rules={[{ required: true, message: 'Please enter your name' }]}
                  >
                    <Input 
                      placeholder="Enter your full name" 
                      size="large"
                      className="modern-input"
                      style={{ height: '48px' }}
                    />
                  </Form.Item>
                </Col>
                
                <Col xs={24} md={12}>
                  <Form.Item
                    label={
                      <Space>
                        <CalendarOutlined style={{ color: '#667eea' }} />
                        Date of Birth
                      </Space>
                    }
                    name="dob"
                    rules={[{ required: true, message: 'Please select your date of birth' }]}
                  >
                    <DatePicker 
                      placeholder="Select your date of birth" 
                      style={{ width: '100%' }}
                      format="YYYY-MM-DD"
                      size="large"
                      suffixIcon={<CalendarOutlined style={{ color: '#667eea' }} />}
                      className="modern-date-picker"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            <Divider style={{ margin: '32px 0' }} />

            {/* Academic Information Section */}
            <div style={{ marginBottom: '32px' }}>
              <Title level={4} style={{ color: '#333', marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
                <BookOutlined style={{ marginRight: '8px', color: '#667eea' }} />
                Academic Information
              </Title>
              
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Form.Item
                   size="large"
                    label={
                      <Space>
                        <TeamOutlined style={{ color: '#667eea' }} />
                        Current Grade
                      </Space>
                    }
                    name="grade"
                    rules={[{ required: true, message: 'Please select your grade' }]}
                  >
                    <Select 
                      placeholder="Select your grade" 
                      size="large"
                      // suffixIcon={<TeamOutlined style={{ color: '#667eea' }} />}
                      className="modern-select"
                    >
                      <Select.Option value="1">Grade 1</Select.Option>
                      <Select.Option value="2">Grade 2</Select.Option>
                      <Select.Option value="3">Grade 3</Select.Option>
                      <Select.Option value="4">Grade 4</Select.Option>
                      <Select.Option value="5">Grade 5</Select.Option>
                      <Select.Option value="6">Grade 6</Select.Option>
                      <Select.Option value="7">Grade 7</Select.Option>
                      <Select.Option value="8">Grade 8</Select.Option>
                      <Select.Option value="9">Grade 9</Select.Option>
                      <Select.Option value="10">Grade 10</Select.Option>
                      <Select.Option value="11">Grade 11</Select.Option>
                      <Select.Option value="12">Grade 12</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                
                {/* Hide institute_code field if it's available from Redux (from /student-auth validation) */}
                {!instituteIdFromRedux && (
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={
                        <Space>
                          <BookOutlined style={{ color: '#667eea' }} />
                          Institute Code
                        </Space>
                      }
                      name="institute_code"
                      rules={[{ required: true, message: 'Please enter your institute code' }]}
                    >
                      <Input 
                        placeholder="Enter your institute code" 
                        size="large"
                        className="modern-input"
                        style={{ height: '48px' }}
                      />
                    </Form.Item>
                  </Col>
                )}
              </Row>
              
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={
                      <Space>
                        <TeamOutlined style={{ color: '#667eea' }} />
                        Division / Section
                      </Space>
                    }
                    name="division"
                    rules={[{ required: false, message: 'Please enter your division' }]}
                  >
                    <Input 
                      placeholder="Enter your division (optional)" 
                      size="large"
                      className="modern-input"
                      style={{ height: '48px' }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Submit Button */}
            <div style={{ textAlign: 'center', marginTop: '40px', paddingTop: '24px', borderTop: '1px solid #f0f0f0' }}>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={submitting} 
                size="large"
                style={{ 
                  minWidth: '200px',
                  height: '48px',
                  borderRadius: '24px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  fontSize: '16px',
                  fontWeight: '600',
                  boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 12px 32px rgba(102, 126, 234, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 8px 24px rgba(102, 126, 234, 0.3)';
                }}
              >
                {submitting ? (
                  <Space>
                    <LoadingOutlined />
                    Creating Profile...
                  </Space>
                ) : (
                  <Space>
                    <CheckCircleOutlined />
                    Complete Profile
                  </Space>
                )}
              </Button>
              
              <div style={{ marginTop: '16px' }}>
                <Text type="secondary" style={{ fontSize: '14px' }}>
                  By completing this form, you agree to our terms of service and privacy policy
                </Text>
              </div>
            </div>
          </Form>
        </Card>
      </div>
    </div>
    </>
  );
};

export default StudentProfileCompletion; 